import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().toISOString().slice(0, 7));
  const [newStaff, setNewStaff] = useState({ name: '', role: '', salary: '' });
  const [editingStaff, setEditingStaff] = useState(null);
  const [loading, setLoading] = useState(false);

  // Convert to Nepali month/year
  const getNepaliMonthYear = (dateStr) => {
    const [year, month] = dateStr.split('-').map(Number);
    const nepaliMonths = ['Baisakh', 'Jestha', 'Ashad', 'Shrawan', 'Bhadra', 'Ashwin',
      'Kartik', 'Mangsir', 'Poush', 'Magh', 'Falgun', 'Chaitra'];
    return `${nepaliMonths[month - 1]} ${year + 57}`;
  };

  // Fetch staff data
  const fetchStaff = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('https://api.pranucollection.com/api/staff');
      setStaff(data);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch staff data', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStaff(); }, []);

  // Add/edit staff
  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingStaff) {
        await axios.put(`https://api.pranucollection.com/api/staff/${editingStaff._id}`, editingStaff);
        Swal.fire('Success', 'Staff updated successfully', 'success');
      } else {
        await axios.post('https://api.pranucollection.com/api/staff', {
          ...newStaff,
          salary: Number(newStaff.salary),
        });
        setNewStaff({ name: '', role: '', salary: '' });
        Swal.fire('Success', 'Staff added successfully', 'success');
      }
      fetchStaff();
      setEditingStaff(null);
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    }
  };

  // Handle payments with validation
  const handlePayment = async (type, staffId, amount, member) => {
    if (!amount || isNaN(amount) || amount <= 0) {
      Swal.fire('Error', 'Please enter a valid amount', 'error');
      return;
    }

    const numericAmount = Number(amount);
    const { advances, payments, remaining } = getSalaryInfo(member);

    try {
      if (type === 'advance') {
        // Check if advance exceeds remaining salary
        if (numericAmount > member.salary - payments) {
          Swal.fire('Error', `Advance cannot exceed remaining salary (Rs ${member.salary - payments})`, 'error');
          return;
        }
        await axios.put(`https://api.pranucollection.com/api/staff/${staffId}/advance`, {
          amount: numericAmount,
          month: currentMonth,
        });
      } else {
        // Check if payment exceeds remaining balance
        if (numericAmount > remaining) {
          Swal.fire('Error', `Payment cannot exceed remaining balance (Rs ${remaining})`, 'error');
          return;
        }
        await axios.put(`https://api.pranucollection.com/api/staff/${staffId}/salary-payment`, {
          amount: numericAmount,
          month: currentMonth,
        });
      }
      fetchStaff();
      Swal.fire('Success', `${type === 'advance' ? 'Advance' : 'Payment'} recorded`, 'success');
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    }
  };

  // Mark as paid
  const handleMarkPaid = async (staffId) => {
    try {
      await axios.post(`https://api.pranucollection.com/api/staff/${staffId}/mark-paid`, {
        month: currentMonth,
      });
      fetchStaff();
      Swal.fire('Success', 'Month marked as paid', 'success');
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    }
  };

  // Delete staff
  const handleDelete = async (staffId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      await axios.delete(`https://api.pranucollection.com/api/staff/${staffId}`);
      fetchStaff();
      Swal.fire('Deleted!', 'Staff member has been removed.', 'success');
    }
  };

  // Calculate salary info
  const getSalaryInfo = (member) => {
    const advances = member.advances?.filter(a => a.month === currentMonth)?.reduce((sum, a) => sum + a.amount, 0) || 0;
    const payments = member.salaryPayments?.filter(p => p.month === currentMonth)?.reduce((sum, p) => sum + p.amount, 0) || 0;
    const remaining = member.salary - payments - advances;
    const isPaid = member.paidMonths?.includes(currentMonth) || remaining <= 0;

    return {
      advances,
      payments,
      remaining: Math.max(0, remaining),
      isPaid,
      totalPaid: payments + advances
    };
  };

  // Format currency
  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount).replace('₹', 'Rs ');
  };

  // Export to Excel
  const exportToExcel = () => {
    if (staff.length === 0) {
      Swal.fire('Info', 'No data to export', 'info');
      return;
    }

    // Prepare worksheet data
    const worksheetData = staff.map(member => {
      const { advances, payments, remaining, totalPaid } = getSalaryInfo(member);
      return {
        'Staff Name': member.name,
        'Role': member.role,
        'Monthly Salary': member.salary,
        'Advances': advances,
        'Payments': payments,
        'Total Paid': totalPaid,
        'Remaining Balance': remaining,
        'Status': remaining <= 0 ? 'Paid' : 'Pending'
      };
    });

    // Add summary row
    const summary = {
      'Staff Name': 'TOTAL',
      'Role': '',
      'Monthly Salary': staff.reduce((sum, m) => sum + m.salary, 0),
      'Advances': staff.reduce((sum, m) => sum + getSalaryInfo(m).advances, 0),
      'Payments': staff.reduce((sum, m) => sum + getSalaryInfo(m).payments, 0),
      'Total Paid': staff.reduce((sum, m) => sum + getSalaryInfo(m).totalPaid, 0),
      'Remaining Balance': staff.reduce((sum, m) => sum + getSalaryInfo(m).remaining, 0),
      'Status': ''
    };

    const data = [...worksheetData, summary];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);

    // Set column widths
    const wscols = [
      { wch: 20 }, // Staff Name
      { wch: 15 }, // Role
      { wch: 15 }, // Monthly Salary
      { wch: 12 }, // Advances
      { wch: 12 }, // Payments
      { wch: 12 }, // Total Paid
      { wch: 18 }, // Remaining Balance
      { wch: 12 }  // Status
    ];
    worksheet['!cols'] = wscols;

    // Add styling to headers
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "4472C4" } }, // Blue background
      alignment: { horizontal: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } }
      }
    };

    // Apply header styles
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!worksheet[cellAddress]) continue;
      worksheet[cellAddress].s = headerStyle;
    }

    // Add borders to all cells
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!worksheet[cellAddress]) worksheet[cellAddress] = { t: 's', v: '' };
        worksheet[cellAddress].s = worksheet[cellAddress].s || {};
        worksheet[cellAddress].s.border = {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        };

        // Right-align numeric columns
        if (C >= 2 && C <= 6) { // Columns C to G (Monthly Salary to Total Paid)
          worksheet[cellAddress].s.alignment = { horizontal: "right" };
        }

        // Format currency for numeric columns
        if (C >= 2 && C <= 6 && R > 0) { // Skip header row
          worksheet[cellAddress].t = 'n';
          worksheet[cellAddress].z = '#,##0';
        }

        // Add color to status cells
        if (C === 7 && R > 0) { // Status column
          const isPaid = worksheet[cellAddress].v === 'Paid';
          worksheet[cellAddress].s.fill = {
            fgColor: { rgb: isPaid ? "C6EFCE" : "FFC7CE" } // Green for Paid, Red for Pending
          };
          worksheet[cellAddress].s.font = {
            color: { rgb: isPaid ? "006100" : "9C0006" }
          };
        }
      }
    }

    // Style summary row
    const summaryRow = range.e.r;
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: summaryRow, c: C });
      if (!worksheet[cellAddress]) continue;
      worksheet[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "F2F2F2" } },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
      if (C >= 2 && C <= 6) {
        worksheet[cellAddress].s.alignment = { horizontal: "right" };
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "Salary Report");

    // Generate Excel file
    XLSX.writeFile(workbook, `Salary_Report_${currentMonth}.xlsx`, { compression: true });
  };
  return (
    <div className="container mx-auto p-4 max-w-6xl">
      <h1 className="text-2xl font-bold mb-6">Staff Salary Management</h1>

      {/* Month Selector and Export */}
      <div className="bg-white p-4 rounded-lg shadow mb-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div className="w-64">
            <label className="block text-sm font-medium mb-1">Select Month:</label>
            <input
              type="month"
              value={currentMonth}
              onChange={(e) => setCurrentMonth(e.target.value)}
              className="w-full p-2 border rounded"
              min="2020-01"
              max="2083-12"
            />
          </div>
          <div className="text-lg font-medium">
            {getNepaliMonthYear(currentMonth)}
          </div>
        </div>
        <button
          onClick={exportToExcel}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
          Export to Excel
        </button>
      </div>

      {/* Add/Edit Staff Form */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <h2 className="text-lg font-semibold mb-4">
          {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
        </h2>
        <form onSubmit={handleStaffSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              value={editingStaff ? editingStaff.name : newStaff.name}
              onChange={(e) => editingStaff
                ? setEditingStaff({ ...editingStaff, name: e.target.value })
                : setNewStaff({ ...newStaff, name: e.target.value })}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Role</label>
            <input
              type="text"
              value={editingStaff ? editingStaff.role : newStaff.role}
              onChange={(e) => editingStaff
                ? setEditingStaff({ ...editingStaff, role: e.target.value })
                : setNewStaff({ ...newStaff, role: e.target.value })}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Salary</label>
            <input
              type="number"
              value={editingStaff ? editingStaff.salary : newStaff.salary}
              onChange={(e) => editingStaff
                ? setEditingStaff({ ...editingStaff, salary: e.target.value })
                : setNewStaff({ ...newStaff, salary: e.target.value })}
              className="w-full p-2 border rounded"
              required
              min="0"
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex-1"
            >
              {editingStaff ? 'Update' : 'Add'}
            </button>
            {editingStaff && (
              <button
                type="button"
                onClick={() => setEditingStaff(null)}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Staff Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading staff data...</div>
        ) : staff.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No staff members found</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-3 text-left">Staff</th>
                <th className="p-3 text-right">Salary</th>
                <th className="p-3 text-right">Advances</th>
                <th className="p-3 text-right">Paid</th>
                <th className="p-3 text-right">Balance</th>
                <th className="p-3 text-center">Status</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map(member => {
                const { advances, payments, remaining, isPaid } = getSalaryInfo(member);
                const maxAdvance = member.salary - payments;
                const maxPayment = remaining;

                return (
                  <tr key={member._id} className="border-b hover:bg-gray-50">
                    <td className="p-3">
                      <div className="font-medium">{member.name}</div>
                      <div className="text-sm text-gray-600">{member.role}</div>
                    </td>
                    <td className="p-3 text-right">{formatMoney(member.salary)}</td>

                    {/* Advances */}
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        <input
                          type="number"
                          defaultValue=""
                          onBlur={(e) => handlePayment('advance', member._id, e.target.value, member)}
                          className="p-1 border rounded w-24 text-right"
                          max={maxAdvance}
                          placeholder="Add advance"
                        />
                        <span className="text-sm min-w-[60px] text-right">{formatMoney(advances)}</span>
                      </div>
                    </td>

                    {/* Payments */}
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-2">
                        <input
                          type="number"
                          defaultValue=""
                          onBlur={(e) => handlePayment('salary', member._id, e.target.value, member)}
                          className="p-1 border rounded w-24 text-right"
                          max={maxPayment}
                          placeholder="Add payment"
                        />
                        <span className="text-sm min-w-[60px] text-right">{formatMoney(payments)}</span>
                      </div>
                    </td>

                    {/* Balance */}
                    <td className="p-3 text-right">
                      <span className={`font-medium ${remaining > 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                        {formatMoney(remaining)}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="p-3 text-center">
                      {isPaid ? (
                        <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                          <span className="w-3 h-3 mr-1">✓</span>
                          Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">
                          Pending
                        </span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => setEditingStaff(member)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Edit
                        </button>
                        {!isPaid && remaining > 0 && (
                          <button
                            onClick={() => handleMarkPaid(member._id)}
                            className="text-green-600 hover:text-green-800 text-sm"
                          >
                            Mark Paid
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(member._id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default StaffManagement;